﻿import { default as classnames } from "classnames";
import { QueryKey, useQuery } from "@tanstack/react-query";
import { FC, useContext, useEffect, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePermissionsContext } from "../../context/permissionContext";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { isLookup, Lookup, MenuOptionValue } from "../../types";
import SvgArrowDown from "../icons/ArrowDown";
import SvgArrowUp from "../icons/ArrowUp";
import SvgClose from "../icons/Close";
import Button from "./Button";
import styles from "./HeaderMenu.module.css";
import HeaderMenuOption, { Option } from "./HeaderMenuOption";
import Icon from "./Icon";

export type FilterOptionsQuery = {
  queryKey: QueryKey;
  queryFn: (context: { signal: AbortSignal }) => Promise<MenuOptionValue[] | null>;
  initialData?: MenuOptionValue[] | null;
};

interface HeaderMenuProps {
  title: string;
  fieldName: string;
  onClose: () => void;
  setOrder?: (order: "DESC" | "ASC") => void;
  optionSuffix?: string;
  filters?: {
    selectedOptions: Exclude<MenuOptionValue, Lookup>[];
    update: (fieldName: string, values: MenuOptionValue[]) => void;
    get: (fieldName: string) => FilterOptionsQuery;
  };
  formatter?: (value: MenuOptionValue) => string | undefined;
}

const HeaderMenu: FC<HeaderMenuProps> = (props: HeaderMenuProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const permissions = usePermissionsContext();

  const filterQuery = props.filters?.get(props.fieldName);
  const queryKey = filterQuery?.queryKey ?? ["header-menu-filter-options", props.fieldName, "disabled"];
  const queryKeyHash = JSON.stringify(queryKey);
  const result = useQuery<MenuOptionValue[] | null, CustomError>({
    queryKey,
    queryFn: ({ signal }) => filterQuery?.queryFn({ signal }) ?? Promise.resolve(null),
    enabled: false,
    initialData: filterQuery?.initialData,
  });
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    filterQuery && result.refetch();
  }, [queryKeyHash]);

  useEffect(() => {
    if (result.error) {
      dispatch(showErrorNotification(i18n, result.error));
    }
  }, [result.error]);

  useEffect(() => {
    if (result.data) {
      const options: Option[] = result.data.map((x) => ({
        value: x,
        selected: !!props.filters?.selectedOptions.filter((y) => (isLookup(x) ? x.id : x) === y).length,
      }));
      setOptions(options);
    }
  }, [result.data]);

  const onClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onClose();
  };

  const update = (options: Option[]) => {
    setOptions(options);
    props.filters?.update(
      props.fieldName,
      options.filter((x) => x.selected).map((x) => x.value)
    );
  };

  const clearFilters = () => {
    const newOptions: Option[] = options.map((x: Option) => {
      return {
        ...x,
        selected: false,
      };
    });
    update(newOptions);
  };

  const updateFilters = (option: Option) => {
    const os: Option[] = options.filter((x: Option) => x.value !== option.value);
    const newOptions: Option[] = [...os, option];
    update(newOptions);
  };

  const orderOptions = (a: Option, b: Option): number => {
    // Handle falsy values early
    if (!a.value && !b.value) return 0; // both are falsy, return 0 for equality
    if (!a.value) return -1; // a is falsy, so a comes before b
    if (!b.value) return 1; // b is falsy, so b comes before a

    // Handle numeric values
    if (typeof a.value === "number" && typeof b.value === "number") {
      return a.value - b.value;
    }

    // Handle Lookup comparisons
    if (isLookup(a.value) && isLookup(b.value)) {
      return a.value.value.localeCompare(b.value.value, permissions.preferredLanguage ?? "en-UK", {
        sensitivity: "base",
      }); // TODO testing.
    }

    const formattedAValue = props.formatter?.(a.value) ?? a.value;
    const formattedBValue = props.formatter?.(b.value) ?? b.value;

    // Handle non-Lookup and non-numeric values as strings
    const valA = formattedAValue.toString();
    const valB = formattedBValue.toString();
    return valA.localeCompare(valB, permissions.preferredLanguage ?? "en-UK", { sensitivity: "base" }); // TODO testing.
  };

  return (
    <div className={styles.menu}>
      <div>
        {!props.setOrder && (
          <div className={classnames(styles.option, styles.wrap)}>{i18n.t.common.columnNotSortable}</div>
        )}
        {props.setOrder && (
          <>
            <div
              className={classnames(styles.option, styles["option-enabled"])}
              onClick={() => props.setOrder?.("ASC")}
            >
              <SvgArrowUp />
              <span>{i18n.t.common.ascending}</span>
            </div>
            <div
              className={classnames(styles.option, styles["option-enabled"])}
              onClick={() => props.setOrder?.("DESC")}
            >
              <SvgArrowDown />
              <span>{i18n.t.common.descending}</span>
            </div>
          </>
        )}
        <div>
          <hr className={styles.separator} />
        </div>
        {!props.filters && (
          <div className={classnames(styles.option, styles.wrap)}>{i18n.t.common.columnNotFilterable}</div>
        )}
        {props.filters && (
          <>
            <div
              className={classnames(
                styles.option,
                styles.wrap,
                options.filter((x) => x.selected).length === 0 ? styles["option-disabled"] : styles["option-enabled"]
              )}
              onClick={clearFilters}
            >
              <Icon glyph={SvgClose} />
              <span>{i18n.t.common.deleteFilter(props.title)}</span>
            </div>
            {!!result.data && (
              <div className={styles.options}>
                {options.sort(orderOptions).map((x, index) => (
                  <HeaderMenuOption
                    optionSuffix={props.optionSuffix}
                    key={`o_${index}`}
                    className={styles["option-enabled"]}
                    option={x}
                    updateFilters={updateFilters}
                    formatter={props.formatter}
                  />
                ))}
              </div>
            )}
          </>
        )}
        <div className={styles["close-button"]}>
          <Button buttonProps={{ onClick: onClose }}>{i18n.t.common.close}</Button>
        </div>
      </div>
    </div>
  );
};

export default HeaderMenu;
